import { useCallback, useEffect, useMemo, useState } from 'react';

import { IListCollectorUsersParams, listCollectorUsers } from '../services/identity/listCollectorUsers';
import { listCareAdvisorUsers } from '../services/identity/listCareAdvisorUsers';
import { IListUsersParams, listAllUsers } from '../services/identity/listAllUsers';
import { UserProfile } from '../services/identity/types/UserProfile';

export type PatientSelectListMode = 'admin' | 'collector' | 'careAdvisor';

export default function usePatientSelectUsers(
  mode: PatientSelectListMode,
  adminParams: Pick<
    IListUsersParams,
    | 'sort_by'
    | 'sort_direction'
    | 'full_name'
    | 'email'
    | 'status'
    | 'type'
    | 'limit'
    | 'organization_ids'
    | 'client_tags'
    | 'page'
    | 'skip'
    | 'date_of_birth_start'
    | 'date_of_birth_end'
  >,
  collectorParams: IListCollectorUsersParams,
): {
  users: UserProfile[];
  totalRecords: number;
  dataLoading: boolean;
  loadError: string | null;
  retry: () => void;
} {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const retry = useCallback(() => setRetryCount(value => value + 1), []);

  const adminKey = useMemo(() => JSON.stringify(adminParams), [adminParams]);
  const collectorKey = useMemo(() => JSON.stringify(collectorParams), [collectorParams]);
  const fetchKey =
    mode === 'collector' ? `collector:${collectorKey}` : mode === 'careAdvisor' ? `careAdvisor:${adminKey}` : `admin:${adminKey}`;

  useEffect(() => {
    const ac = new AbortController();
    setDataLoading(true);
    setLoadError(null);

    const run = async (): Promise<void> => {
      try {
        if (mode === 'admin') {
          const res = await listAllUsers(adminParams, ac.signal);
          if (ac.signal.aborted) return;
          setTotalRecords(res.total);
          setUsers([...res.nodes]);
        } else if (mode === 'careAdvisor') {
          const res = await listCareAdvisorUsers(adminParams);
          if (ac.signal.aborted) return;
          setTotalRecords(res.total);
          setUsers([...res.nodes]);
        } else {
          const res = await listCollectorUsers(collectorParams);
          if (ac.signal.aborted) return;
          setTotalRecords(res.total);
          setUsers([...(res.nodes ?? [])]);
        }
      } catch {
        if (!ac.signal.aborted) setLoadError('Unable to load patients. Please try again.');
        if (!ac.signal.aborted) {
          setTotalRecords(0);
          setUsers([]);
        }
      } finally {
        if (!ac.signal.aborted) setDataLoading(false);
      }
    };

    void run();

    return (): void => {
      ac.abort();
    };
    // fetchKey encodes mode + serialized params for the active list only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, retryCount]);

  return { users, totalRecords, dataLoading, loadError, retry };
}
